import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/api";
import { getDaysUntil } from "@/utils/helpers";
import { BILLABLE_CASE_STATUSES, getPracticeBilling } from "@/lib/practiceBilling";

/**
 * Custom hook for managing all Gestionale data and operations
 * Centralizes state management and API operations
 */
export function useGestionaleData() {
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Main data states
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [settings, setSettings] = useState(null);
  const [customDeadlinesAll, setCustomDeadlinesAll] = useState([]);
  const [billingItemsAll, setBillingItemsAll] = useState([]);
  const [numberingSequences, setNumberingSequences] = useState({
    "Gestionale Studio": 1,
    "Gestionale Agenzia": 1
  });

  // Current authenticated user and linked operator
  const [currentUser, setCurrentUser] = useState(null);
  const [currentOperator, setCurrentOperator] = useState(null);

  // Computed: current operator ID from linked operator
  const currentOperatorId = currentOperator?.id || null;

  // Load all data from API
  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setLoadingError(null);

    try {
      // First, get current user with operator details
      const user = await api.getCurrentUser();
      setCurrentUser(user);

      // Set linked operator from user response
      if (user?.operator) {
        setCurrentOperator(user.operator);
      } else {
        setCurrentOperator(null);
      }

      // Load all data in parallel - each with individual error handling
      const [
        clientsData,
        casesData,
        invoicesData,
        deadlinesData,
        operatorsData,
        numberingData,
        settingsData,
        customDeadlinesData,
        billingItemsData
      ] = await Promise.all([
        api.getClients().catch(() => []),
        api.getCases().catch(() => []),
        api.getInvoices().catch(() => []),
        api.getDeadlines().catch(() => []),
        api.getOperators().catch(() => []),
        api.getNumbering().catch(() => null),
        api.getSettings().catch(() => null),
        api.getCustomDeadlines().catch(() => []),
        api.getBillingItems().catch(() => [])
      ]);

      setClients(clientsData || []);
      setCases(casesData || []);
      setInvoices(invoicesData || []);
      setDeadlines(deadlinesData || []);
      setOperators(operatorsData || []);
      setCustomDeadlinesAll(customDeadlinesData || []);
      setBillingItemsAll(billingItemsData || []);
      setSettings(settingsData || {});
      setNumberingSequences(numberingData || {
        "Gestionale Studio": 1,
        "Gestionale Agenzia": 1
      });
    } catch (error) {
      console.error("Error loading data:", error);
      setLoadingError(error.message || "Errore nel caricamento dei dati");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Update numbering from backend response
  const updateNumberingFromBackend = useCallback((practiceType, caseNumber) => {
    const match = caseNumber.match(/\d+$/);
    if (match) {
      const num = parseInt(match[0], 10);
      // Set the current max number (generatePreviewCaseNumberSync will add +1)
      setNumberingSequences(prev => ({
        ...prev,
        [practiceType]: num
      }));
    }
  }, []);

  // Link current user to an operator
  const handleLinkOperator = useCallback(async (operatorId) => {
    try {
      await api.linkOperator(operatorId);
      const user = await api.getCurrentUser();
      setCurrentUser(user);
      setCurrentOperator(user.operator);
      return true;
    } catch (error) {
      throw error;
    }
  }, []);

  // Get operator name by ID
  const getOperatorName = useCallback((operatorId) => {
    if (!operatorId) return "—";
    const operator = operators.find(op => op.id === operatorId);
    return operator ? operator.fullName : "—";
  }, [operators]);

  // Computed values
  const activeCases = useMemo(() =>
    cases.filter(c => c.status !== "Conclusa"),
    [cases]
  );

  const casesToBill = useMemo(() => {
    const regularCases = cases
      .filter(c => BILLABLE_CASE_STATUSES.has(c.status))
      .filter(c => !c.billingExcluded)
      .map(c => {
        const billing = getPracticeBilling(c, invoices, clients);
        return {
          ...c,
          feeMode: billing.mode,
          _billing: billing,
          _fee: billing.agreedAmount,
          _invoicedAmount: billing.invoicedAmount,
          _residuo: billing.residualAmount,
          _fullyInvoiced: billing.fullyInvoiced,
        };
      })
      .filter(c => {
        if (c._fullyInvoiced) return false;
        if (c._fee === 0) return true;
        return c._residuo > 0;
      })
      .map(c => ({
        ...c,
        _isPartiallyInvoiced: c._invoicedAmount > 0,
      }));
    
    // Completed client deadlines as virtual "cases" for billing
    const completedDeadlines = customDeadlinesAll
      .filter(d => d.status === "completata")
      .map(d => ({
        id: `deadline_${d.id}`,
        _isClientDeadline: true,
        clientId: d.clientId || "",
        clientName: d.clientName,
        caseNumber: "",
        subject: d.deadlineType,
        practiceArea: d.notes || "",
        practiceType: d.practiceType === "studio" ? "Gestionale Studio" : "Gestionale Agenzia",
        status: "Completata",
        estimatedFee: 0,
        finalFee: 0,
        openingDate: d.deadlineDate,
        priority: "Media",
        deadlineSourceId: d.id,
      }));

    // Billing plan items with status da_fatturare
    const planItems = billingItemsAll
      .filter(bi => bi.billingStatus === "da_fatturare")
      .map(bi => ({
        id: `billing_${bi.id}`,
        _isBillingItem: true,
        billingItemId: bi.id,
        clientId: bi.clientId || "",
        clientName: bi.clientName,
        caseNumber: "",
        subject: bi.description || "Piano di Fatturazione",
        practiceArea: `Periodo: ${bi.period}`,
        practiceType: bi.managementType === "studio" ? "Gestionale Studio" : "Gestionale Agenzia",
        status: "Completata",
        estimatedFee: bi.grossAmount || 0,
        finalFee: bi.grossAmount || 0,
        openingDate: bi.createdAt?.slice(0, 10) || "",
        priority: "Media",
        paymentMethod: bi.paymentMethod || "",
      }));
    
    return [...regularCases, ...completedDeadlines, ...planItems];
  }, [cases, customDeadlinesAll, billingItemsAll, invoices, clients]);

  const readyToBill = useMemo(() =>
    casesToBill.filter(
      c => (c._billing?.residualAmount ?? c.finalFee ?? c.estimatedFee ?? 0) > 0
    ),
    [casesToBill]
  );

  const missingBillingData = useMemo(() =>
    casesToBill.filter(c => !c.estimatedFee && !c.finalFee),
    [casesToBill]
  );

  const totalToBill = useMemo(() =>
    readyToBill.reduce(
      (sum, c) => sum + (c._billing?.residualAmount ?? c.finalFee ?? c.estimatedFee ?? 0),
      0
    ),
    [readyToBill]
  );

  const unpaidInvoices = useMemo(() =>
    invoices.filter(inv => inv.status !== "Pagata"),
    [invoices]
  );

  const totalUnpaid = useMemo(() =>
    unpaidInvoices.reduce((sum, inv) => sum + (inv.totale || 0), 0),
    [unpaidInvoices]
  );

  const criticalDeadlines = useMemo(() =>
    deadlines.filter(d => {
      if (d.status === "completata") return false;
      const days = getDaysUntil(d.dueDate);
      return days !== null && days <= 3;
    }),
    [deadlines]
  );

  const upcomingDeadlines = useMemo(() =>
    deadlines.filter(d => {
      if (d.status === "completata") return false;
      const days = getDaysUntil(d.dueDate);
      return days !== null && days > 0 && days <= 7;
    }),
    [deadlines]
  );

  return {
    // Loading states
    isLoading,
    loadingError,
    isSaving,
    setIsSaving,

    // Data
    clients,
    setClients,
    cases,
    setCases,
    invoices,
    setInvoices,
    deadlines,
    setDeadlines,
    operators,
    setOperators,
    numberingSequences,
    settings,

    // User/Operator
    currentUser,
    currentOperator,
    currentOperatorId,
    handleLinkOperator,
    getOperatorName,

    // Computed
    activeCases,
    casesToBill,
    readyToBill,
    missingBillingData,
    totalToBill,
    unpaidInvoices,
    totalUnpaid,
    criticalDeadlines,
    upcomingDeadlines,

    // Actions
    loadAllData,
    updateNumberingFromBackend
  };
}
